import React, { useEffect, useState } from 'react';
import { FaCopy, FaUsers } from 'react-icons/fa';
import { SERVER_HOST, STATUS_REFRESH_MS } from '../../config/server';
import { useT } from '../../i18n';
import { fetchMinecraftStatus } from '../../services/minecraftStatus';
import './ServerStatus.css';

/**
 * @param {{ variant?: 'hero' | 'panel' }} props
 */
const ServerStatus = ({ variant = 'hero' }) => {
  const t = useT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await fetchMinecraftStatus();
        if (cancelled) return;
        setStatus(data);
        setError(false);
      } catch {
        if (cancelled) return;
        setError(true);
        setStatus(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const intervalId = window.setInterval(load, STATUS_REFRESH_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(SERVER_HOST);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const online = status?.online === true;
  const stateLabel = loading
    ? t('status.checking')
    : error
      ? t('status.unavailable')
      : online
        ? t('status.online')
        : t('status.offline');
  const stateClass = loading
    ? 'is-loading'
    : error
      ? 'is-unknown'
      : online
        ? 'is-online'
        : 'is-offline';

  const playersText =
    online && status?.playersOnline != null
      ? status.playersMax != null
        ? `${status.playersOnline} / ${status.playersMax}`
        : `${status.playersOnline}`
      : null;

  return (
    <div className={`server-status server-status--${variant} ${stateClass}`}>
      <div className="server-status__meta">
        <span className="server-status__badge">
          <span className="server-status__dot" aria-hidden="true" />
          {stateLabel}
        </span>

        {playersText && (
          <span className="server-status__players">
            <FaUsers aria-hidden="true" />
            {playersText} {t('status.players')}
          </span>
        )}

        {online && status?.version && (
          <span className="server-status__version">{status.version}</span>
        )}
      </div>

      <div className="server-status__ip">
        <div className="server-status__ip-text">
          <span className="server-status__ip-label">{t('status.ipLabel')}</span>
          <code className="server-status__ip-value">{SERVER_HOST}</code>
        </div>
        <button
          type="button"
          className="server-status__copy"
          onClick={copyIp}
          aria-label={t('status.copyAria')}
        >
          <FaCopy aria-hidden="true" />
          {copied ? t('status.copied') : t('status.copy')}
        </button>
      </div>
    </div>
  );
};

export default ServerStatus;
